/**
 * Filters for browser JS errors that we don't want to report to browser monitoring (CloudWatch RUM),
 * e.g. because they're caused by things outside our control and are thus just noise.
 */

/** The details of a browser JS error that we can filter by */
export type BrowserErrorDetails = {
  /** Error type name, e.g. "TypeError" (if known) */
  name?: string;
  message: string;
  /** URL of the script where the error was thrown (if known) */
  sourceURL?: string;
  stack?: string;
};

const browserExtensionURLPattern =
  /(chrome|moz|safari|safari-web|ms-browser)-extension:\/\//;

/**
 * Returns whether all frames of the given stack trace are on line 1 of the page document, which is
 * where Safari attributes code that apps (e.g. Facebook's in-app browser on iOS) inject into the page.
 * We have no scripts of our own there (it's the doctype), as long as our HTML isn't minified into a
 * single line.
 */
const isInjectedScriptStack = (stack: string, pageURL: string) => {
  const documentURL = pageURL.split(/[?#]/)[0];
  return stack
    .split('\n')
    .every((frame) => frame.includes(`${documentURL}:1:`));
};

/**
 * Returns whether the given error should not be reported
 *
 * @param pageURL URL of the current page
 */
export const shouldIgnoreBrowserError = (
  { name, message, sourceURL, stack }: BrowserErrorDetails,
  pageURL: string = window.location.href
): boolean => {
  // Errors thrown by browser extensions
  if (
    browserExtensionURLPattern.test(sourceURL ?? '') ||
    browserExtensionURLPattern.test(stack ?? '')
  ) {
    return true;
  }

  // Errors thrown by browser extensions' content scripts calling the WebExtension messaging API
  // (which pages can't call, so these never come from our code)
  if (/\bruntime\.sendMessage\(\)/.test(message)) {
    return true;
  }

  // Errors thrown by the Giosg chat widget (a third-party script that we embed)
  if ([message, sourceURL, stack].some((value) => /giosg/i.test(value ?? ''))) {
    return true;
  }

  // Errors thrown by cross-origin scripts, whose details the browser hides from us
  if (/^Script error\.?$/.test(message) && !stack) {
    return true;
  }

  // Promise rejections without a reason (e.g. `reject()` called by a third-party script), which
  // can't be traced to any code (there's no stack trace)
  if (/^(undefined|null)$/.test(message) && !name && !stack) {
    return true;
  }

  // Benign ResizeObserver warnings that browsers report as errors
  if (
    /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/.test(
      message
    )
  ) {
    return true;
  }

  // Errors thrown by scripts that Android in-app browsers (e.g. Facebook's and Instagram's) inject
  // into the page, when the app has already removed the native object the script calls
  if (/Java object is gone/.test(message)) {
    return true;
  }

  // Safari's generic SyntaxError (e.g. from a third-party script's unhandled `Response.json()`
  // rejection) without a stack trace, so we can't attribute it to any code of ours
  if (
    /^The string did not match the expected pattern\.?$/.test(message) &&
    !stack
  ) {
    return true;
  }

  // Errors whose stack trace has only native code frames, i.e. no code of ours is involved (e.g.
  // `json@[native code]` from a third-party script's unhandled `Response.json()` rejection in Safari)
  if (
    stack &&
    stack.split('\n').every((frame) => /\[native code\]/.test(frame))
  ) {
    return true;
  }

  // Errors thrown by third-party scripts (e.g. browser extensions) that read the page's
  // structured data (JSON-LD) and expect it to have an `@context`
  if (/\["@context"\]/.test(message)) {
    return true;
  }

  // Errors thrown by scripts that apps inject into the page
  if (stack && isInjectedScriptStack(stack, pageURL)) {
    return true;
  }

  return false;
};

/** Returns the error details of a thrown value (usually, but not necessarily, an `Error`) */
export const browserErrorDetailsFromThrown = (
  thrown: unknown
): BrowserErrorDetails =>
  thrown instanceof Error
    ? { name: thrown.name, message: thrown.message, stack: thrown.stack }
    : { message: String(thrown) };

/** Returns the error details of a window `error` or `unhandledrejection` event */
export const browserErrorDetailsFromEvent = (
  event: ErrorEvent | PromiseRejectionEvent
): BrowserErrorDetails => {
  if ('reason' in event) {
    return browserErrorDetailsFromThrown(event.reason);
  }
  return {
    ...browserErrorDetailsFromThrown(event.error),
    message: event.message,
    sourceURL: event.filename || undefined,
  };
};
